import axios from "axios";
import {
  getToken,
  storeToken,
  clearToken,
  getRefreshToken,
  storeRefreshToken,
  clearRefreshToken,
  clearUser,
  getUser,
} from "../utils/localStorage";

/* =============================================================
   CONFIG
============================================================= */
const API_BASE_URL = "https://aadhavacademy.com/api";

const AUTH_EXCLUDED_URLS = [
  "/auth/login.php",
  "/auth/refresh_token.php",
];

/* =============================================================
   AXIOS INSTANCES
============================================================= */
const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

/* =============================================================
   GLOBAL REFRESH CALLBACK
============================================================= */
let onTokenRefresh = null;
export const setTokenRefreshHandler = (cb) => (onTokenRefresh = cb);

/* =============================================================
   REFRESH QUEUE
============================================================= */
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) =>
    error ? p.reject(error) : p.resolve(token)
  );
  failedQueue = [];
};

/* =============================================================
   REFRESH TOKEN LOGIC
============================================================= */
const refreshAccessToken = async () => {
  isRefreshing = true;

  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error("Missing refresh token");

    const res = await refreshClient.post(
      "/auth/refresh_token.php",
      { refresh_token: refreshToken }
    );

    const { access_token, refresh_token, user_data } = res.data;
    if (!access_token) throw new Error("Invalid refresh response");

    storeToken(access_token);
    if (refresh_token) storeRefreshToken(refresh_token);

    axiosClient.defaults.headers.Authorization =
      `Bearer ${access_token}`;

    if (onTokenRefresh) {
      onTokenRefresh({
        user: user_data || getUser(),
        access_token,
        refresh_token: refresh_token || refreshToken,
      });
    }

    processQueue(null, access_token);
    return access_token;

  } catch (err) {
    processQueue(err, null);
    clearToken();
    clearRefreshToken();
    clearUser();
    if (onTokenRefresh) onTokenRefresh(null);
    window.location.href = "/login";
    throw err;
  } finally {
    isRefreshing = false;
  }
};

/* =============================================================
   REQUEST INTERCEPTOR
============================================================= */
axiosClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/* =============================================================
   RESPONSE INTERCEPTOR (STRICT 401)
============================================================= */
axiosClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    const isAuthEndpoint = AUTH_EXCLUDED_URLS.some((u) =>
      originalRequest?.url?.includes(u)
    );

    if (
      error?.response?.status === 401 &&
      !originalRequest?._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosClient(originalRequest);
        });
      }

      const newToken = await refreshAccessToken();
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosClient(originalRequest);
    }

    return Promise.reject(error);
  }
);

export default axiosClient;
